import i18next from 'i18next';
import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';

import {
    addNewMailScopeToMailKey,
    deleteMailScopeFromMailKey,
    getMailTemplateAdmin,
    getPreviewOfMailTemplate,
    sendMailPreview,
} from 'src/api/mail-admin';
import { enqueueSnackbarAction } from '../notifications/actions';
import {
    ADD_NEW_MAILSCOPE_TO_MAILKEY,
    DELETE_MAILSCOPE,
    FETCH_MAIL_TEMPLATES,
    FETCH_PREVIEW_OF_MAIL_TEMPLATE,
    requestFetchMailTemplatesFailure,
    requestFetchMailTemplatesSuccess,
    requestMailTemplates,
    requestPreviewOfMailTemplate,
    requestPreviewOfMailTemplateFailure,
    requestPreviewOfMailTemplateSuccess,
    requestSendMailPreviewToReceiver,
    requestSendMailPreviewToReceiverFailure,
    requestSendMailPreviewToReceiverSuccess,
    SEND_MAIL_PREVIEW_TO_RECEIVER,
} from './actions';

function* fetchMailTemplatesSaga(action: AnyAction): SagaIterator {
    yield put(requestMailTemplates());

    const { error, response } = yield call(getMailTemplateAdmin);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.mails.loadingMailsFailure') + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestFetchMailTemplatesFailure(error));
    } else {
        yield put(
            enqueueSnackbarAction({
                message: response.statusText || i18next.t('it:pages.mails.loadingMailsSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(requestFetchMailTemplatesSuccess(response));
    }
}

function* addNewMailscopeToMailKeySaga(action: AnyAction): SagaIterator {
    const { error, response } = yield call(addNewMailScopeToMailKey, action.mailScopeRequest);
    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.mails.mailScopeRow.addMailScopeFailure') + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(
            enqueueSnackbarAction({
                message: response.statusText || i18next.t('it:pages.mails.mailScopeRow.addMailScopeSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* deleteMailscopeSaga(action: AnyAction): SagaIterator {
    yield put(requestMailTemplates());

    const { error, response } = yield call(deleteMailScopeFromMailKey, action.mailScopeId);
    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.mails.mailScopeRow.deleteMailScopeFailure') + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(
            enqueueSnackbarAction({
                message: response.statusText || i18next.t('it:pages.mails.mailScopeRow.deleteMailScopeSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* fetchPreviewOfMailTemplateSaga(action: AnyAction): SagaIterator {
    yield put(requestPreviewOfMailTemplate());

    const { error, response } = yield call(
        getPreviewOfMailTemplate,
        action.mailTemplate,
        action.mailScopes,
        action.region,
        action.language
    );
    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.mails.mailPreview.createMailPreviewFailure') + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestPreviewOfMailTemplateFailure(error));
    } else {
        yield put(
            enqueueSnackbarAction({
                message: response.statusText || i18next.t('it:pages.mails.mailPreview.createMailPreviewSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(requestPreviewOfMailTemplateSuccess(response));
    }
}

function* sendMailPreviewToReceiverSaga(action: AnyAction): SagaIterator {
    yield put(requestSendMailPreviewToReceiver());

    const { error, response } = yield call(
        sendMailPreview,
        action.mailKey,
        action.receivers,
        action.scopes,
        action.region,
        action.language
    );
    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('it:pages.mails.mailPreview.sendMailPreviewFailure') + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestSendMailPreviewToReceiverFailure(error));
    } else {
        yield put(
            enqueueSnackbarAction({
                message: response.statusText || i18next.t('it:pages.mails.mailPreview.sendMailPreviewSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(requestSendMailPreviewToReceiverSuccess(response));
    }
}

export function* generateFetchMailTemplatesWatcher() {
    yield takeLatest(FETCH_MAIL_TEMPLATES, fetchMailTemplatesSaga);
}

export function* generateAddNewMailscopeForMailKeyWatcher() {
    yield takeLatest(ADD_NEW_MAILSCOPE_TO_MAILKEY, addNewMailscopeToMailKeySaga);
}

export function* generateDeleteMailscopeWatcher() {
    yield takeLatest(DELETE_MAILSCOPE, deleteMailscopeSaga);
}

export function* generateFetchPreviewOfMailTemplateWatcher() {
    yield takeLatest(FETCH_PREVIEW_OF_MAIL_TEMPLATE, fetchPreviewOfMailTemplateSaga);
}

export function* generateSendMailPreviewToReceiverWatcher() {
    yield takeLatest(SEND_MAIL_PREVIEW_TO_RECEIVER, sendMailPreviewToReceiverSaga);
}
